import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { BaseService } from "../../shared/services/base.service";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BidLogResult, BidLog } from "../models/bid.log.interface";
import { CustomerSummary, CustomerSummaryResult } from "../models/customer.summary.interface";
import { LeadSource, LeadSourceResult } from "../models/lead.source.interface";
import { LostQuote, LostQuoteResult } from "../models/lost.quote.interface";
import { DatePipe } from "@angular/common";
import { DateReport } from "../models/date.report.interface";
import { PayrollSummary, PayrollSummaryResult } from "../models/payroll.summary.interface";
import { JobCost, JobCostResult } from "../models/job.cost.interface";
import { Router } from "@angular/router";
import { EmailLog, EmailLogResult } from "../models/email.log.interface";
import { CustomerMetric, CustomerMetricResult } from "../models/customer.metric.interface";
import { InventoryParameters, InventoryResult } from "../models/Inventory.interface";
import { MessageLog, MessageLogResult } from "../models/message.log.interface";

@Injectable()
export class ReportService extends BaseService {

  constructor(private datepipe: DatePipe, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getBidLogExport(bidLog: BidLog, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/bidLog?" + params.toString(), bidLog);
  }

  getBidLogReport(bidLog: BidLog): Observable<BidLogResult> {
    return this.httpPost<BidLogResult>("/report/bidLog", bidLog);
  }

  getBidLogSummary(bidLog: BidLog, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/bidLogSummary?" + params.toString(), bidLog);
  }

  getCustomerSummaryExport(customerSummary: CustomerSummary, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/customerSummary?" + params.toString(), customerSummary);
  }

  getCustomerSummaryReport(customerSummary: CustomerSummary): Observable<CustomerSummaryResult> {
    return this.httpPost<CustomerSummaryResult>("/report/customerSummary", customerSummary);
  }

  getLeadSourceExport(leadSource: LeadSource, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/leadSource?" + params.toString(), leadSource);
  }

  getLeadSourceReport(leadSource: LeadSource): Observable<LeadSourceResult> {
    return this.httpPost<LeadSourceResult>("/report/leadSource", leadSource);
  }

  getLostQuoteExport(lostQuote: LostQuote, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/lostQuote?" + params.toString(), lostQuote);
  }

  getLostQuoteReport(lostQuote: LostQuote): Observable<LostQuoteResult> {
    return this.httpPost<LostQuoteResult>("/report/lostQuote", lostQuote);
  }

  getEmailLogExport(emailLog: EmailLog, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile("/report/emailLog?" + params.toString(), emailLog);
  }

  getEmailLogReport(emailLog: EmailLog): Observable<EmailLogResult[]> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('timezone', timezone.toString());

    return this.httpPost<EmailLogResult[]>("/report/emailLog?" + params.toString(), emailLog);
  }

  getMessageLogReport(messageLog: MessageLog): Observable<MessageLogResult[]> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('timezone', timezone.toString());

    return this.httpPost<MessageLogResult[]>("/report/smsMessageLog?" + params.toString(), messageLog);
  }

  getJobCostExport(jobCost: JobCost, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile(`/report/jobCost?` + params.toString(), jobCost);
  }

  getJobCostReport(jobCost: JobCost): Observable<JobCostResult> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('timezone', timezone.toString());

    return this.httpPost<JobCostResult>(`/report/jobCost?${params.toString()}`, jobCost);
  }

  getPayrollSummaryExport(payrollSummary: PayrollSummary, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile(`/report/payrollSummary?${params.toString()}`, payrollSummary);
  }

  getPayrollSummaryReport(payrollSummary: PayrollSummary): Observable<PayrollSummaryResult> {
    return this.httpPost<PayrollSummaryResult>(`/report/payrollSummary`, payrollSummary);
  }

  getCustomerMetricExport(customerMetric: CustomerMetric, format: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/dealer/customerMetric?" + params.toString(), customerMetric);
  }

  getCustomerMetricReport(customerMetric: CustomerMetric): Observable<CustomerMetricResult> {
    return this.httpPost<CustomerMetricResult>("/dealer/customerMetric", customerMetric);
  }

  getInventoryExport(inventoryParms: InventoryParameters, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/item/inventory?" + params.toString(), inventoryParms);
  }

  getInventoryReport(inventoryParms: InventoryParameters): Observable<InventoryResult[]> {    
    return this.httpPost<InventoryResult>(`/report/inventory`, inventoryParms);
  }

  getInventoryReportExport(inventoryParms: InventoryParameters, format: string, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString())
      .append('locale', locale);

    return this.httpPostFile(`/report/inventory?${params.toString()}`, inventoryParms);
  }

  setupParameters(report: DateReport): void {
    if (report.year == null && report.startYear ==  null && report.endYear == null) {
      report.startDate = null;
      report.endDate = null;
      return;
    }

    let startDate = new Date();
    if (report.startYear != null) {
      
      startDate.setFullYear(report.startYear, report.startMonth, 1);
    }
    else {
      startDate.setFullYear(report.year, report.startMonth, 1);
    }

    report.startDate = this.datepipe.transform(startDate, 'yyyy-MM-ddT00:00:00');

    let endDate = new Date();

    if (report.endYear != null) {
      endDate.setFullYear(report.endYear, report.endMonth + 1, 1);
    }
    else {
      endDate.setFullYear(report.year, report.endMonth + 1, 1);
    }

    endDate.setDate(endDate.getDate() - 1);

    report.endDate = this.datepipe.transform(endDate, 'yyyy-MM-ddT23:59:59');
  }
}
